import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Key, AlertCircle, CheckCircle2, Shield, Lock } from 'lucide-react';
import axios from 'axios';
import './Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

/* ── Must be outside component to avoid focus-loss on every keystroke ── */
const PwField = ({ name, label, showKey, placeholder, form, show, errors, onSet, onToggle }) => (
  <div className="form-group">
    <label>{label}</label>
    <div className="password-wrap">
      <input
        type={show[showKey] ? 'text' : 'password'}
        value={form[name]}
        onChange={e => onSet(name, e.target.value)}
        placeholder={placeholder}
        className={errors[name] ? 'input-error' : ''}
        autoComplete="new-password"
      />
      <button type="button" className="pwd-eye" onClick={() => onToggle(showKey)}>
        {show[showKey] ? <EyeOff size={16}/> : <Eye size={16}/>}
      </button>
    </div>
    {errors[name] && <span className="field-error"><AlertCircle size={11}/> {errors[name]}</span>}
  </div>
);

const AdminResetPassword = () => {
  const [form,    setForm]    = useState({ resetSecret: '', newPassword: '', confirmPassword: '' });
  const [show,    setShow]    = useState({ secret: false, pw: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState('');

  const handleSet    = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: '' })); setServerError(''); };
  const handleToggle = (k)    => setShow(p => ({ ...p, [k]: !p[k] }));

  const validate = () => {
    const e = {};
    if (!form.resetSecret.trim()) e.resetSecret = 'Reset secret is required';
    if (!form.newPassword || form.newPassword.length < 8) e.newPassword = 'Minimum 8 characters';
    if (form.newPassword !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        resetSecret: form.resetSecret,
        newPassword: form.newPassword,
      });
      setSuccess(true);
    } catch (err) {
      setServerError(err.response?.data?.message || 'Invalid request. Check your reset secret.');
    } finally { setLoading(false); }
  };

  const shared = { form, show, errors, onSet: handleSet, onToggle: handleToggle };

  return (
    <div className="admin-login">
      <div className="admin-login__box" style={{maxWidth:'420px'}}>

        {success ? (
          <div style={{textAlign:'center',padding:'8px 0'}}>
            <CheckCircle2 size={48} color="var(--success)" style={{margin:'0 auto 16px',display:'block'}}/>
            <h2 style={{fontFamily:'var(--font-display)',fontSize:'22px',color:'var(--navy)',marginBottom:'10px'}}>
              Password Reset!
            </h2>
            <p style={{fontSize:'14px',color:'var(--text-3)',marginBottom:'24px',lineHeight:1.6}}>
              Your admin password has been updated. You can now log in with your new password.
            </p>
            <Link to="/admin/login" className="btn-primary" style={{display:'flex',justifyContent:'center'}}>
              Go to Login
            </Link>
          </div>
        ) : (
          <>
            <div className="admin-login__header">
              <div className="admin-login__icon"><Lock size={28} color="var(--primary)"/></div>
              <h2>Reset Password</h2>
              <p className="login-sub">Enter your reset secret and new password</p>
            </div>

            <div style={{background:'rgba(59,130,246,0.07)',border:'1px solid rgba(59,130,246,0.2)',borderRadius:'6px',padding:'11px 14px',marginBottom:'20px',fontSize:'12.5px',color:'var(--text-2)',display:'flex',gap:'8px',alignItems:'flex-start'}}>
              <Shield size={14} style={{color:'var(--info)',flexShrink:0,marginTop:'1px'}}/>
              <span>The reset secret is set on your server as the <strong>ADMIN_RESET_SECRET</strong> environment variable.</span>
            </div>

            {serverError && (
              <div className="login-error"><AlertCircle size={15}/> {serverError}</div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Secret key field — inline since it's unique */}
              <div className="form-group">
                <label><Key size={11} style={{marginRight:5}}/>Reset Secret</label>
                <div className="password-wrap">
                  <input
                    type={show.secret ? 'text' : 'password'}
                    value={form.resetSecret}
                    onChange={e => handleSet('resetSecret', e.target.value)}
                    placeholder="Your ADMIN_RESET_SECRET value"
                    className={errors.resetSecret ? 'input-error' : ''}
                    autoComplete="off"
                  />
                  <button type="button" className="pwd-eye" onClick={() => handleToggle('secret')}>
                    {show.secret ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
                {errors.resetSecret && <span className="field-error"><AlertCircle size={11}/> {errors.resetSecret}</span>}
              </div>

              <PwField {...shared} name="newPassword"     label="New Password"         showKey="pw"      placeholder="At least 8 characters"/>
              <PwField {...shared} name="confirmPassword" label="Confirm New Password" showKey="confirm" placeholder="Re-enter new password"/>

              <button type="submit" className="btn-primary admin-login__btn" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>

            <div className="admin-login__back">
              <Link to="/admin/login">← Back to Login</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminResetPassword;
